
import { supabase } from '@/integrations/supabase/client';
import DOMPurify from 'dompurify';

export const rateLimitCheck = async (actionType: string, maxAttempts: number = 5) => {
  try {
    const { data, error } = await supabase.rpc('check_rate_limit', {
      user_identifier: await getUserIdentifier(),
      action_type: actionType,
      max_attempts: maxAttempts
    });
    
    if (error) {
      console.error('Rate limit check error:', error);
      return false;
    }
    
    return data;
  } catch (error) {
    console.error('Rate limit check failed:', error);
    return false;
  }
};

const getUserIdentifier = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || 'anonymous';
};

// Enhanced input sanitization with comprehensive XSS protection
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  // Use DOMPurify for comprehensive HTML sanitization
  const cleaned = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
  
  // Additional sanitization for common attack patterns
  return cleaned
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/on\w+=/gi, '')
    .trim();
};

// Validate email format securely
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254;
};

// Enhanced file validation
export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  if (!file || !file.type) return false;
  return allowedTypes.includes(file.type);
};

export const validateFileSize = (file: File, maxSizeInMB: number): boolean => {
  if (!file) return false;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes && file.size > 0;
};

// Secure file name validation
export const validateFileName = (fileName: string): boolean => {
  if (!fileName || typeof fileName !== 'string') return false;
  
  // Check for dangerous characters and patterns
  const dangerousPatterns = [
    /\.\./,  // Directory traversal
    /[<>:"|?*]/,  // Invalid filename characters
    /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i,  // Reserved Windows names
    /^\./,  // Hidden files
    /\.$/ // Trailing dots
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(fileName)) && 
         fileName.length <= 255 && 
         fileName.trim().length > 0;
};

// Enhanced account lockout check using audit logs
export const checkAccountLocked = async (email: string): Promise<boolean> => {
  try {
    // Check audit logs for failed login attempts
    const { data: auditLogs } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('action', 'login_failed')
      .gte('timestamp', new Date(Date.now() - 30 * 60 * 1000).toISOString())
      .order('timestamp', { ascending: false });

    if (!auditLogs) return false;

    // Count failed attempts for this email from audit logs
    const failedAttemptsCount = auditLogs.filter(log => {
      const details = log.details as any;
      return details?.email === email || log.user_email === email;
    }).length;

    return failedAttemptsCount >= 5;
  } catch (error) {
    console.error('Account lock check failed:', error);
    return false;
  }
};

// Enhanced failed login logging using audit logs
export const logFailedLogin = async (email: string, ipAddress?: string, userAgent?: string) => {
  try {
    // Log failed login attempt to audit logs
    const { error: auditError } = await supabase
      .from('audit_logs')
      .insert({
        user_id: 'system',
        action: 'login_failed',
        user_email: email,
        details: {
          email,
          ip_address: ipAddress || getClientIP(),
          user_agent: userAgent || getUserAgent(),
          timestamp: new Date().toISOString()
        }
      });
    
    if (auditError) {
      console.error('Failed to log failed login attempt:', auditError);
    }
  } catch (error) {
    console.error('Failed to log failed login attempt:', error);
  }
};

// Get client IP address (best effort)
export const getClientIP = (): string => {
  // This is limited in browser environment, but we can try
  return 'unknown';
};

// Get user agent
export const getUserAgent = (): string => {
  return navigator.userAgent || 'unknown';
};

// Validate form inputs with comprehensive checks
export const validateFormInput = (value: string, type: 'text' | 'email' | 'password' | 'url' = 'text'): {
  isValid: boolean;
  sanitized: string;
  errors: string[];
} => {
  const errors: string[] = [];
  let sanitized = sanitizeInput(value);
  
  // Common validation
  if (!sanitized || sanitized.length === 0) {
    errors.push('Field cannot be empty');
  }
  
  if (sanitized.length > 1000) {
    errors.push('Input too long (max 1000 characters)');
    sanitized = sanitized.substring(0, 1000);
  }
  
  // Type-specific validation
  switch (type) {
    case 'email':
      if (sanitized && !validateEmail(sanitized)) {
        errors.push('Invalid email format');
      }
      break;
    case 'password':
      if (sanitized && sanitized.length < 8) {
        errors.push('Password must be at least 8 characters');
      }
      if (sanitized && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(sanitized)) {
        errors.push('Password must contain uppercase, lowercase, and number');
      }
      break;
    case 'url':
      try {
        if (sanitized && !sanitized.startsWith('http://') && !sanitized.startsWith('https://')) {
          errors.push('URL must start with http:// or https://');
        } else if (sanitized) {
          new URL(sanitized);
        }
      } catch {
        errors.push('Invalid URL format');
      }
      break;
  }
  
  return {
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};

// Security headers utility
export const getSecurityHeaders = () => {
  return {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:; connect-src 'self' https://*.supabase.co; frame-ancestors 'none';"
  };
};

// Enhanced security event logging
export const logSecurityEvent = async (eventType: string, description: string, metadata?: any) => {
  try {
    const { error } = await supabase.from('security_logs').insert({
      event_type: eventType,
      event_description: description,
      ip_address: getClientIP(),
      user_agent: getUserAgent(),
      metadata: metadata || {}
    });
    
    if (error) {
      console.error('Failed to log security event:', error);
    }
  } catch (error) {
    console.error('Failed to log security event:', error);
  }
};

// Role validation helper
export const validateRoleUpdate = (currentRole: string, newRole: string, isSuperAdmin: boolean): boolean => {
  // Only super admins can change roles
  if (currentRole !== newRole && !isSuperAdmin) {
    return false;
  }
  
  // Valid roles
  const validRoles = ['data_entry', 'analyst', 'manager', 'admin'];
  return validRoles.includes(newRole);
};
